import { cn } from '../lib/utils';

export type ProgressDotsProps = {
  count: number;
  /** The step on show. */
  index: number;
  /**
   * How far through the current step, 0 to 1, drawn as the mint fill in its
   * bar. Leave it out when nothing is advancing (autoplay off, reduced motion)
   * and the bar is drawn full.
   */
  progress?: number;
  onSelect: (index: number) => void;
  /** Each dot's accessible name. Defaults to "Show 2 of 3". */
  labelFor?: (index: number) => string;
  className?: string;
};

/**
 * A step indicator for something that advances on its own: a frosted pill
 * of dots, with the current step stretched into a bar that fills as its time
 * runs out. Steps already passed are mint, the ones still to come pale.
 *
 * Made to sit over a photograph, on the dark panel. The frosting is the panel
 * at under half strength with the picture blurred through it, so the dots stay
 * legible over a bright sky and a dark wall alike.
 */
export function ProgressDots({
  count,
  index,
  progress = 1,
  onSelect,
  labelFor = (i) => `Show ${i + 1} of ${count}`,
  className,
}: ProgressDotsProps) {
  if (count < 2) return null;

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-button bg-panel/45 px-2 backdrop-blur-md',
        className,
      )}
    >
      {Array.from({ length: count }, (_, i) => {
        const active = i === index;
        return (
          <button
            key={i}
            type="button"
            onClick={() => onSelect(i)}
            aria-label={labelFor(i)}
            aria-current={active || undefined}
            // A 24px-tall target around a 5px mark, which is the size WCAG 2.2
            // asks for at AA; the padding is transparent.
            className={cn(
              'grid h-6 place-items-center px-1',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-on-panel',
            )}
          >
            <span
              className={cn(
                'relative block h-1.25 overflow-hidden rounded-button',
                'transition-[width,background-color] duration-(--duration-base) ease-standard',
                'motion-reduce:transition-none',
                active ? 'w-7.5 bg-on-panel/28' : 'w-1.25',
                !active && (i < index ? 'bg-brand/90' : 'bg-on-panel/55'),
              )}
            >
              {active && (
                // Scaled rather than sized, so the fill is a transform the
                // compositor moves and not a width laid out every frame.
                <span
                  className="absolute inset-0 origin-left bg-brand"
                  style={{ transform: `scaleX(${Math.min(Math.max(progress, 0), 1)})` }}
                />
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
